import { BaseElement } from '../base-element';
import { AfterViewInit, Component, ElementRef, forwardRef, Input, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { FileItem, FileUploader, ParsedResponseHeaders } from 'ng2-file-upload';
import { SessionTokenService } from '../../../../session/service/session-token';
import { HttpConfig } from '../../../http/config';
import { ToastrService } from 'ngx-toastr';
import { FormEvent } from '../../../crud/form/form-event.enum';
import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { mergeMap, tap } from 'rxjs/operators';
import { Md5Service } from '../../../misc/service/md5-service';
import { from } from 'rxjs';
import { ErrorCodes } from '../../../enum/api-errors';

@Component({
    selector: 'upload-browse',
    templateUrl: './upload-browse.component.html',
    styleUrls: ['./upload-browse.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => UploadBrowseComponent),
            multi: true,
        },
    ],
})
export class UploadBrowseComponent extends BaseElement<string> implements OnInit, AfterViewInit {
    private httpClientNoIntercept: HttpClient;
    protected job: any;
    protected progress: number = 0;
    protected uploading: boolean = false;
    protected uploader: FileUploader;
    protected isValid: boolean = true;

    @ViewChild('uploadEl', { static: true }) uploadElRef: ElementRef;
    @Input() file: any;
    @Input() data: any;
    @Input() url: string;
    @Input() previewTemplate: TemplateRef<any>;
    @Input() formControlName: string;
    @Input() relatedTo: string;
    @Input() aspectRatio: string;
    @Input() type: string;
    @Input() imageWidth?: number;
    @Input() imageHeight?: number;
    @Input() onFileUploaded?: any;
    private md5sum;
    private validator;
    private apiResponse: any;

    constructor(
        private sessionTokenService: SessionTokenService,
        private config: HttpConfig,
        private toastrService: ToastrService,
        private httpClient: HttpClient,
        private handler: HttpBackend,
        private toastr: ToastrService,
    ) {
        super();
        this.httpClientNoIntercept = new HttpClient(handler);
    }

    ngAfterViewInit(): void {
        if (this.channel) {
            this.channel.subscribe((event: FormEvent) => {
                if (event === FormEvent.SUBMIT) {
                    if (this.file) {
                        this.form.controls[this.formControlName].setErrors(null);
                    }
                    if (this.form.controls[this.formControlName].invalid) {
                        this.toggleInvalidClass(true);
                    } else {
                        this.toggleInvalidClass(false);
                    }
                }
            });
        }
    }

    writeValue(value: string): void {
        this.innerValue = value;
        // handling plain string filename
        if (!this.file) {
            this.file = value;
        }
    }

    ngOnInit(): void {
        const control = this.form.controls[this.formControlName];
        this.uploader = new FileUploader({});

        this.uploader.onAfterAddingFile = (fileItem) => {
            const reader = new FileReader();
            reader.onload = (event: any) => {
                const img = new Image();

                img.src = event.target.result;

                if (this.imageWidth && this.imageHeight) {
                    img.onload = () => {
                        if (img.width !== this.imageWidth || img.height !== this.imageHeight) {
                            this.toastrService.error(
                                `Image dimensions should be exactly ${this.imageWidth}x${this.imageHeight}px`
                            );
                            this.uploader.removeFromQueue(fileItem);
                            this.isValid = false;
                            this.uploading = false;
                        }
                    };
                };
                reader.readAsDataURL(fileItem._file);
            };

            if (!this.isValid) {
                this.form.controls[this.formControlName].setErrors({ invalid: true });
            }
        }

        control.valueChanges.subscribe((value: any) => {
            if (value && value.id) {
                this.file = value;
                control.setValue(value.id, { emitEvent: false });
            } else if (typeof value === 'string') {
                this.file = value;
                control.setValue(value, { emitEvent: false });
            }
        });
    }

    private upload(file: File): void {
        const fileExtension = file.name.split('.').pop();
        this.httpClient
            .post<any>(this.url, { extension: fileExtension })
            .pipe(
                tap((response) => {
                    this.apiResponse = response;
                }),
                mergeMap((resp) => {
                    const headers = new HttpHeaders({
                        'Content-Type': this.getMimeType(fileExtension),
                    });
                    return this.httpClientNoIntercept.put(resp.signedUrl, file, { headers }).pipe(
                        mergeMap(() => from(Md5Service.compute(file))),
                        tap((hash) => (this.md5sum = hash)),
                        mergeMap(() => {
                            return this.httpClient.put<any>(this.url, {
                                ...resp,
                                originalFileName: file.name,
                                fileType: this.type,
                                extension: fileExtension,
                                md5sum: this.md5sum,
                                relatedTo: this.relatedTo,
                                aspectRatio: this.aspectRatio,
                            });
                        })
                    );
                })
            )
            .subscribe(
                (data) => {
                    this.onFileUpload(data);
                    this.uploading = false;
                },
                (error) => {
                    let message: string;
                    if (error.error && error.error.message) {
                        message = error.error.message;
                    }
                    if (error.error && error.error.code) {
                        message = ErrorCodes[error.error.code] || error.error.code;
                    }
                    this.toastr.error(message || 'Upload error');
                }
            );
    }

    onChange(): void {
        this.toggleInvalidClass(false);
    }

    fileSelected(files: any): void {
        if (!this.isValid) {
            return;
        }
        this.validator = this.form.controls[this.formControlName].validator;
        const file: File = files[0];
        this.uploading = true;

        this.upload(file);
    }

    private onFileUpload(file: any) {
        this.form.controls[this.formControlName].clearValidators();
        this.form.controls[this.formControlName].setValidators(this.validator);
        this.form.controls[this.formControlName].setValue(file);

        this.onChange();

        if (this.onFileUploaded && typeof this.onFileUploaded === 'function') {
            this.onFileUploaded(file, this.formControlName, this.apiResponse);
        }
    }

    protected toggleInvalidClass(flag: boolean): void {
        this.customClass = Object.assign(this.customClass, { 'is-invalid': flag });
    }

    private getMimeType(fileExtension: string) {
        switch (fileExtension.toLowerCase()) {
            case 'mp4':
                return 'video/mp4';
            case 'webm':
                return 'video/webm';
            case 'm4a':
                return 'audio/mp4';
            case 'mp3':
                return 'audio/mpeg';
            case 'gif':
                return 'image/gif';
            case 'jpeg':
            case 'jpg':
                return 'image/jpeg';
            case 'png':
                return 'image/png';
            default:
                this.toastr.error('Wrong file extension');
                throw new Error('wrong file extension');
        }
    }
}
